'use es6';

import { List } from 'immutable';
import { Axis, XYVisualization } from 'reporting-visualizations/visualizations/xy/xyRecords';
import { buildXYInsightLayerOverrides } from 'reporting-visualizations/visualizations/xy/xyUtils';
import { getDateResolution, getReportColorEncoding, getReportXEncoding, getReportYEncoding, getReportYMultiEncoding } from '../../utils/report-utils';
import { applyScaleToAxis } from '../apply-scale-to-axis';
import { applyTypeToAxis } from '../apply-type-to-axis';
import { computeAssignments, computePropertyAssignments } from '../color/assign';
import { collectDimensionBuckets, collectMeasureBuckets } from '../color/bucket';
import { doMeasuresHaveSameFormattingType, getGoalLayer, getMeasureAxisLabel, getMultiMeasureFormatting, getTodayLine } from './shared';
import { AccumulateAlong, AccumulateTransform } from 'reporting-visualizations/transforms/transformRecords';
import { reportSupportsGoals } from '../../resolve/goal';
import { getEncodingFormatType, getEncodingFormatParameters, getEncodingColumnAlias } from '../../utils/visual-utils';
import { toVisualizationOrder } from '../../modify/sorts/report-sort-utils';
import { getColorPalette } from '../color/utils';
import { getDeprecatedDefaultPalettes } from '../color/palettes';
import { getDatasetInsightsMetricName, getDatasetInsightsRangeName, getPrimaryDataset } from 'reporting-data/v2/dataset/datasetGetters';
import { findDatasetInsights } from 'reporting-data/datasetInsights';

/**
 * @param {Column?} colorColumn
 * @param {List<Column>} measureColumns
 * @param {Dataset} dataset
 * @param {ColorOption} colorOption
 * @param {BrandKitSnowflakePalettes} brandKitPalettes
 * @param {ReportingPalettes} palettes
 * @returns high charts color encoding
 */
const toColor = (colorColumn, measureColumns, dataset, colorOption, colorEncoding, brandKitPalettes, palettes) => {
  const order = colorColumn && toVisualizationOrder(colorColumn, dataset);
  const buckets = colorColumn === undefined && measureColumns ? collectMeasureBuckets(measureColumns, dataset) : collectDimensionBuckets(colorColumn, dataset);
  const {
    scheme
  } = getColorPalette(colorOption, brandKitPalettes, (palettes === null || palettes === void 0 ? void 0 : palettes.DEFAULT_PALETTES) || getDeprecatedDefaultPalettes('line-mapper-fallback'), palettes === null || palettes === void 0 ? void 0 : palettes.DefaultCategorical);
  const assignments = computeAssignments(buckets, scheme, colorOption.assignments);
  if (assignments.isEmpty()) {
    return undefined;
  }
  const dashStyleAssignments = computePropertyAssignments(buckets, scheme.dashStylePatterns);
  if (colorColumn === undefined) {
    const alias = measureColumns && measureColumns.size === 1 ? measureColumns.get(0).get('alias') : colorEncoding && colorEncoding.column && colorEncoding.column.alias;
    return {
      default: assignments.get(alias),
      defined: assignments.toJS(),
      definedDashStyles: dashStyleAssignments && dashStyleAssignments.toJS()
    };
  }
  const colorId = colorColumn.alias;
  return {
    field: colorId,
    defined: assignments.toJS(),
    definedDashStyles: dashStyleAssignments && dashStyleAssignments.toJS(),
    order,
    formattingType: getEncodingFormatType(colorEncoding),
    formattingObject: getEncodingFormatParameters(colorEncoding)
  };
};
const mapSingleMeasureEncodingVisual = (report, datasets, fiscalYearMonthName, brandKitPalettes, palettes) => {
  const primaryDataset = getPrimaryDataset(datasets);
  const insightDatasets = findDatasetInsights(datasets);
  const hasInsightDatasets = insightDatasets && insightDatasets.count();
  const x = getReportXEncoding(report);
  const y = getReportYEncoding(report);
  const color = getReportColorEncoding(report);
  const visualOptions = report.visual.options;
  const {
    showDataLabels,
    showMarkers,
    showToday,
    yMinMax,
    yAxisType,
    color: colorOption,
    goalLineValue,
    legendPosition,
    legendWidth,
    accumulate
  } = visualOptions;
  const yAxisKey = 'left';
  const xField = x && x.column.alias;
  const yField = y && y.column.alias;
  const colorField = color && color.column.alias;
  const accumulateAlong = AccumulateAlong({
    field: xField,
    /*
    TODO: We only allow ascending accumulations due to
     reporting-snowflake/static-1.49972/js/relational/validate/constraints/visuals/check-sort-is-accumulate-friendly.js:4
     but there is no longer a technical reason why we can't support descending accumulations now.
      We should update this in the near future.
      For now, it is okay to hardcode this
    */
    direction: 'ascending'
  });
  const buildPrimaryLayer = () => {
    return {
      dataset: 'primary',
      mark: {
        type: 'line',
        marker: {
          enabled: showMarkers
        }
      },
      x: x ? {
        type: 'category',
        field: xField,
        blend: 'bottom',
        order: toVisualizationOrder(x.column, primaryDataset),
        formattingType: getEncodingFormatType(x),
        formattingObject: getEncodingFormatParameters(x)
      } : {
        type: 'scaffold',
        blend: 'bottom'
      },
      y: y ? {
        type: 'linear',
        field: yField,
        blend: yAxisKey,
        fill: true,
        formattingType: getEncodingFormatType(y),
        formattingObject: getEncodingFormatParameters(y)
      } : {
        type: 'scaffold',
        blend: yAxisKey
      },
      color: toColor(report.columns.get(colorField), y ? List.of(report.columns.get(yField)) : undefined, primaryDataset, colorOption, color, brandKitPalettes, palettes),
      transform: accumulate ? [AccumulateTransform({
        groupBy: [colorField],
        field: yField,
        along: accumulateAlong
      })] : []
    };
  };
  const primaryLayer = buildPrimaryLayer();
  const insightsLayers = hasInsightDatasets ? insightDatasets.reduce((result, insightDataset, key) => {
    const insightLayerOverrides = buildXYInsightLayerOverrides(insightDataset, key, visualOptions);
    result.push(Object.assign({}, primaryLayer, insightLayerOverrides.primary));
    return result;
  }, []) : [];
  const goalLayers = goalLineValue && reportSupportsGoals(report) ? [getGoalLayer(showMarkers, x, primaryDataset, color, accumulate, accumulateAlong)] : [];
  const layers = [...goalLayers, primaryLayer, ...insightsLayers];
  const maybeDateResolution = getDateResolution(xField, report);
  const xColumn = report.columns.get(xField);
  return XYVisualization({
    type: 'XY',
    data: datasets,
    style: {
      dataLabels: showDataLabels,
      legendPosition,
      legendWidth,
      x: showToday && maybeDateResolution ? {
        bottom: {
          rules: {
            plotLines: [getTodayLine(xColumn, fiscalYearMonthName)]
          }
        }
      } : undefined,
      y: {
        [yAxisKey]: applyTypeToAxis(applyScaleToAxis(Axis({}), yMinMax), yAxisType)
      },
      dateResolution: maybeDateResolution
    },
    layers
  });
};
const mapMultiMeasureEncodingVisual = (report, datasets, fiscalYearMonthName, brandKitPalettes, palettes) => {
  const primaryDataset = getPrimaryDataset(datasets);
  const insightDatasets = findDatasetInsights(datasets);
  const hasInsightDatasets = insightDatasets && insightDatasets.count();
  const x = getReportXEncoding(report);
  const yMulti = getReportYMultiEncoding(report);
  const color = getReportColorEncoding(report);
  const visualOptions = report.visual.options;
  const {
    showDataLabels,
    showMarkers,
    showToday,
    yMinMax,
    yAxisType,
    color: colorOption,
    goalLineValue,
    legendPosition,
    legendWidth,
    accumulate,
    yAxisLabel: labelSetting
  } = visualOptions;
  const yAxisKey = 'left';
  const yAxisLabel = getMeasureAxisLabel(labelSetting, yMulti, primaryDataset);
  const xField = x && getEncodingColumnAlias(x);
  const colorField = color && getEncodingColumnAlias(color);
  const maybeDateResolution = getDateResolution(xField, report);
  const accumulateAlong = AccumulateAlong({
    field: xField,
    /*
    TODO: We only allow ascending accumulations due to
     reporting-snowflake/static-1.49972/js/relational/validate/constraints/visuals/check-sort-is-accumulate-friendly.js:4
     but there is no longer a technical reason why we can't support descending accumulations now.
      We should update this in the near future.
      For now, it is okay to hardcode this
    */
    direction: 'ascending'
  });
  const allMeasuresHaveSameFormattingType = doMeasuresHaveSameFormattingType(yMulti, primaryDataset);
  const formattingObjectMap = getMultiMeasureFormatting(yMulti);
  const useMultiEncodingFormattingObject = yMulti.size > 1;
  const buildYMultiPrimaryLayer = (yEncoding, yField) => {
    return {
      dataset: 'primary',
      mark: {
        type: 'line',
        marker: {
          enabled: showMarkers
        }
      },
      x: x ? {
        type: 'category',
        field: xField,
        blend: 'bottom',
        order: toVisualizationOrder(x.column, primaryDataset),
        formattingType: getEncodingFormatType(x),
        formattingObject: getEncodingFormatParameters(x)
      } : {
        type: 'scaffold',
        blend: 'bottom'
      },
      y: {
        type: 'linear',
        field: yField,
        blend: yAxisKey,
        fill: true,
        formattingObjectMap: useMultiEncodingFormattingObject ? formattingObjectMap : undefined,
        formattingType: allMeasuresHaveSameFormattingType ? getEncodingFormatType(yEncoding) : undefined,
        formattingObject: allMeasuresHaveSameFormattingType ? getEncodingFormatParameters(yEncoding) : undefined
      },
      color: toColor(color && color.column, yMulti.map(encoding => encoding.column), primaryDataset, colorOption, yMulti.size > 1 ? yEncoding : color, brandKitPalettes, palettes),
      transform: accumulate ? [AccumulateTransform({
        groupBy: [colorField],
        field: yField,
        along: accumulateAlong
      })] : []
    };
  };
  const primaryLayers = yMulti.map(yEncoding => {
    const yField = getEncodingColumnAlias(yEncoding);
    return buildYMultiPrimaryLayer(yEncoding, yField);
  });
  const insightsLayers = hasInsightDatasets ? insightDatasets.reduce((result, insightDataset, key) => {
    const insightLayerOverrides = buildXYInsightLayerOverrides(insightDataset, key, visualOptions);
    const yField = getDatasetInsightsMetricName(insightDataset);
    const yEncoding = yMulti.find(encoding => encoding.column.alias === yField);
    const primaryLayer = buildYMultiPrimaryLayer(yEncoding, yField);
    result.push(Object.assign({}, primaryLayer, insightLayerOverrides.primary));
    if (insightLayerOverrides.range) {
      const rangeField = getDatasetInsightsRangeName(insightDataset);
      const rangeLayer = buildYMultiPrimaryLayer(yEncoding, rangeField);
      result.push(Object.assign({}, rangeLayer, insightLayerOverrides.range));
    }
    return result;
  }, []) : [];
  const goalLayers = goalLineValue && reportSupportsGoals(report) ? [getGoalLayer(showMarkers, x, primaryDataset, color, accumulate, accumulateAlong)] : [];
  const layers = [...goalLayers, ...primaryLayers, ...insightsLayers];
  return XYVisualization({
    type: 'XY',
    data: datasets,
    style: {
      dataLabels: showDataLabels,
      legendPosition,
      legendWidth,
      showTotals: allMeasuresHaveSameFormattingType,
      x: showToday && maybeDateResolution ? {
        bottom: {
          rules: {
            plotLines: [getTodayLine(x.column, fiscalYearMonthName)]
          }
        }
      } : undefined,
      y: {
        [yAxisKey]: applyTypeToAxis(applyScaleToAxis(Axis({}), yMinMax), yAxisType).set('title', yAxisLabel)
      },
      dateResolution: maybeDateResolution
    },
    layers
  });
};
export const mapLineVisual = (report, datasets, _reportOptions, {
  fiscalYearMonthName,
  brandKitPalettes,
  palettes
}) => {
  const yMulti = getReportYMultiEncoding(report);
  const insightDatasets = findDatasetInsights(datasets);
  const hasInsightDatasets = insightDatasets && insightDatasets.count();
  if (hasInsightDatasets) {
    // Force reports with insights datasets to use multi-measure code path
    // so we don't need to update the single measure path to handle forecasting
    //
    // The intent is to update all of the xy snowflake mappers to use the multi-measure code path
    // Context: https://git.hubteam.com/HubSpot/reporting/pull/15428
    const reportWithYMulti = yMulti && yMulti.size > 0 ? report : report.setIn(['visual', 'encodings', 'y_multi'], List.of(report.getIn(['visual', 'encodings', 'y']))).deleteIn(['visual', 'encodings', 'y']);
    return mapMultiMeasureEncodingVisual(reportWithYMulti, datasets, fiscalYearMonthName, brandKitPalettes, palettes);
  } else if (yMulti && yMulti.size > 0) {
    return mapMultiMeasureEncodingVisual(report, datasets, fiscalYearMonthName, brandKitPalettes, palettes);
  }
  return mapSingleMeasureEncodingVisual(report, datasets, fiscalYearMonthName, brandKitPalettes, palettes);
};
export const __TESTABLE__ = {
  toColor,
  mapMultiMeasureEncodingVisual,
  mapSingleMeasureEncodingVisual
};